import { useState } from 'react';
import { getApiUrl } from '../lib/api';
import { motion } from 'framer-motion';

interface TripReportFormProps {
  countryCode: string;
  onSubmit: () => void;
  onCancel: () => void;
}

export default function TripReportForm({ countryCode, onSubmit, onCancel }: TripReportFormProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [rating, setRating] = useState('5');
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim() || !content.trim()) {
      setError('Title and content are required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/trip-reports'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          country_code: countryCode,
          title: title.trim(),
          content: content.trim(),
          experience_rating: parseInt(rating),
          tags: tags.trim() ? tags.split(',').map(t => t.trim()) : [],
          device_fingerprint: localStorage.getItem('device_fp') || 'unknown'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit trip report');
      }

      setTitle('');
      setContent('');
      setRating('5');
      setTags('');
      onSubmit();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-white font-bold">Share Your Experience</h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-white/40 hover:text-white/70 text-sm"
        >
          Close
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        <label className="text-white/70 text-sm font-semibold">Trip Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., 'Amazing week in Amsterdam' or 'First time in Canada'"
          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-cannabis-500"
        />
      </div>

      <div className="space-y-2">
        <label className="text-white/70 text-sm font-semibold">Your Experience</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Share details about your cannabis tourism experience, favorite spots, local customs, what you learned..."
          rows={5}
          className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-cannabis-500 resize-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-white/70 text-sm font-semibold">Experience Rating</label>
          <select
            value={rating}
            onChange={(e) => setRating(e.target.value)}
            className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cannabis-500"
          >
            <option value="5">★★★★★ Amazing</option>
            <option value="4">★★★★ Great</option>
            <option value="3">★★★ Good</option>
            <option value="2">★★ Okay</option>
            <option value="1">★ Poor</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-white/70 text-sm font-semibold">Tags (comma-separated)</label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., 'relaxing, nature, coffee shops'"
            className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-cannabis-500 text-sm"
          />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-2 rounded-lg bg-cannabis-600 hover:bg-cannabis-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Submitting...' : 'Share Trip Report'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10 font-semibold transition-colors"
        >
          Cancel
        </button>
      </div>
    </motion.form>
  );
}
